// src/services/tables/tileGridComponent.js
// Reusable grid component for tile displays (benchmarking, comparisons, etc.)
// NOTE: TilePerformanceGrid (for pitchers) has been moved to Tile1Performance30dPitcher.js

import React from 'react';
import { dynastyTheme } from '../colorService';
import { formatBattingAvg } from '../statFormatUtils';

/**
 * Hitter performance grid component (for hitter tiles)
 * NOW DYNAMIC - builds columns from returned data
 */
export const TileHitterGrid = ({ 
  data, 
  showLeague = true 
}) => {
  // Return early if no data
  if (!data || !data.player) {
    return (
      <div className={`text-sm ${dynastyTheme.classes.text.neutralLight}`}>
        No performance data available
      </div>
    );
  }

  const { player, mlb_benchmark, league_benchmark, comparisons } = data;

  // DYNAMIC: Build headers and statKeys from actual returned data
  const playerStats = player.stats || {};
  const statKeys = Object.keys(playerStats);
  
  // Map backend field names to display labels
  const STAT_LABELS = {
    'batting_avg': 'AVG',
    'obp': 'OBP',
    'slg': 'SLG',
    'ops': 'OPS',
    'home_runs': 'HR',
    'rbi': 'RBI',
    'stolen_bases': 'SB',
    'runs': 'R',
    'hits': 'H',
    'doubles': '2B',
    'triples': '3B',
    'walks': 'BB',
    'strikeouts': 'K',
    'caught_stealing': 'CS',
    'plate_appearances': 'PA',
    'at_bats': 'AB'
  };
  
  const headers = statKeys.map(key => STAT_LABELS[key] || key.toUpperCase());

  // Format number display
  const formatStat = (value, key) => {
    if (value === null || value === undefined) return '0';

    // Rate stats: 3 decimals, no leading zero
    if (['batting_avg', 'obp', 'slg', 'ops'].includes(key)) {
      return formatBattingAvg(value);
    }

    // Counting stats: whole numbers
    return Math.round(value).toString();
  };

  // Get color based on comparison
  const getStatColor = (comparison, stat) => {
    if (!comparison || !comparison[stat]) return dynastyTheme.classes.text.white;
    if (comparison[stat] === 'better') return dynastyTheme.classes.text.success;
    if (comparison[stat] === 'worse') return dynastyTheme.classes.text.error;
    return dynastyTheme.classes.text.white;
  };

  // Get trend indicator
  const getTrendIndicator = (comparisons) => {
    if (!comparisons?.vs_mlb) return '';
    const betterCount = Object.values(comparisons.vs_mlb).filter(v => v === 'better').length;
    const totalStats = Object.keys(comparisons.vs_mlb).length;
    if (betterCount > totalStats * 0.6) return '↑';
    if (betterCount < totalStats * 0.4) return '↓';
    return '→';
  };

  const trendIndicator = getTrendIndicator(comparisons);
  const trendColor = trendIndicator === '↑' ? dynastyTheme.classes.text.success : 
                     trendIndicator === '↓' ? dynastyTheme.classes.text.error : 
                     dynastyTheme.classes.text.neutral;

  return (
    <div className="space-y-1">
      {/* Headers */}
      <div className="grid grid-cols-7 gap-1 text-sm font-semibold">
        <div></div>  {/* Empty cell for row labels */}
        {headers.map(h => (
          <div key={h} className={`text-center ${dynastyTheme.classes.text.primary}`}>
            {h}
          </div>
        ))}
      </div>

      {/* Player row */}
      <div className="grid grid-cols-7 gap-1 text-sm items-center">
        <div className={`${dynastyTheme.classes.text.white} flex items-center`}>
          You:
          {trendIndicator && (
            <span className={`ml-1 text-sm ${trendColor}`}>{trendIndicator}</span>
          )}
        </div>
        {statKeys.map(key => (
          <div 
            key={key}
            className={`text-center ${getStatColor(comparisons?.vs_mlb, key)}`}
          >
            {formatStat(player.stats?.[key], key)}
          </div>
        ))}
      </div>
      
      {/* MLB benchmark row */}
      {mlb_benchmark && (
        <div className="grid grid-cols-7 gap-1 text-sm">
          <div className={dynastyTheme.classes.text.neutralLight}>MLB:</div>
          {statKeys.map(key => (
            <div key={key} className={`text-center ${dynastyTheme.classes.text.neutralLight}`}>
              {formatStat(mlb_benchmark.hitting?.[key], key)}
            </div>
          ))}
        </div>
      )}
      
      {/* League benchmark row (if present and enabled) */}
      {showLeague && league_benchmark && (
        <div className="grid grid-cols-7 gap-1 text-sm">
          <div className={dynastyTheme.classes.text.neutralLight}>League:</div>
          {statKeys.map(key => (
            <div key={key} className={`text-center ${dynastyTheme.classes.text.neutralLight}`}>
              {formatStat(league_benchmark.hitting?.[key], key)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Export hitter grid component only
// TilePerformanceGrid has been moved to Tile1Performance30dPitcher.js
export default {
  TileHitterGrid
};